import * as _ from "lodash";
import { all, call, delay, put, select } from "redux-saga/effects";

import actions from "@/actions";
import type { MarkerPosition } from "@/models/marker-position";
import * as MarkerService from "@/services/marker";
import { Api } from "@/utils";

import loadFloorSpaceCatalogs from "../load-floor-space-catalogs";
import loadLights from "../load-lights";
import loadMarkers from "../load-markers";
import saveLightGroups from "../save-light-groups";
import saveToApi from "../save-to-api/save-to-api";
import syncFloorSpace from "../sync-floor-space";

function* refreshLights({ floorSpaceId }) {
    const { luminaireSummary } = yield call(
        Api.get,
        `v1/floor-spaces/${floorSpaceId}/with-lightsmaps`,
    );

    const positioningType = yield select(
        ({ map }) =>
            map.general.settings?.fixtureDetails?.positioningType ?? "",
    );

    yield call(loadLights, { luminaireSummary, positioningType });
}

interface SaveMarkersParams {
    /**
     * Markers that have been created or modified.
     */
    changedMarkers: MarkerPosition[];
    /**
     * Markers that have been deleted.
     */
    deletedMarkers: MarkerPosition[];
}

/**
 * Saves changed markers and deletes removed markers via the marker service.
 *
 * @param {SaveMarkersParams} params - The parameters for saving markers.
 * @yields Calls the marker service save and delete APIs.
 */
function* saveMarkers({ changedMarkers, deletedMarkers }: SaveMarkersParams) {
    const deleteIds = deletedMarkers.filter((m) => m.id > 0).map((m) => m.id);

    yield all([
        changedMarkers.length > 0
            ? call(MarkerService.save, changedMarkers)
            : null,
        deleteIds.length > 0 ? call(MarkerService.deleteMany, deleteIds) : null,
    ]);

    if (deletedMarkers.length > 0) {
        yield put(
            actions.window.showMessage(
                `${deletedMarkers.length} marker(s) were successfully deleted.`,
                "success",
            ),
        );
    }
}

/**
 * Saves all pending light and settings changes to the API, including deletions and group geometries.
 *
 * @yields Calls save APIs and dispatches status updates.
 */
function* saveChanges() {
    try {
        const {
            deletedLights,
            deletedMarkers,
            lights,
            markers,
            site,
            ...payload
        } = yield select(({ map }) => ({
            deletedLights: map.values.global.present.deletedLights,
            deletedMarkers: map.values.global.present.deletedMarkers,
            floorSpace: map.general.settings.convertToAPI(),
            lights: _.filter(
                map.values.global.present.lights,
                (light) =>
                    light.changed || light.computedTag !== light._persistedTag,
            ),
            markers: _.filter(
                map.values.global.present.markers,
                (marker: MarkerPosition) => marker.changed,
            ),
            presentLightsData: map.values.global.present.lights,
            site: map.general.site,
        }));

        const { groupGeometries, lightGroups } = yield select(({ map }) => ({
            groupGeometries: map.values.global.present.groupGeometries,
            lightGroups: map.general.lightGroups,
        }));

        yield put(
            actions.map.general.setSyncingStatus({
                name: "syncing",
                message: "Attempting to sync changes to the cloud…",
            }),
        );

        payload.luminaireSummary = lights.map((light) => light.convertToAPI());
        payload.deleteLuminaires = deletedLights
            .filter((d) => d.positionId > 0)
            .map((d) => d.positionId);

        const result = yield call(saveToApi, payload);

        if (!result) {
            return;
        }

        const { floorSpaceID: floorSpaceId } = result.floorSpace;

        if (deletedLights.length > 0) {
            yield put(
                actions.window.showMessage(
                    `${deletedLights.length} light(s) were successfully deleted.`,
                    "success",
                ),
            );
        }

        yield call(saveLightGroups, {
            siteId: site.siteID,
            groupGeometries,
            lightGroups,
        });
        yield call(syncFloorSpace, floorSpaceId);

        yield all([
            call(refreshLights, { floorSpaceId }),
            call(saveMarkers, {
                changedMarkers: markers,
                deletedMarkers,
            }),
        ]);

        yield call(loadMarkers, { floorSpaceId });
        yield call(loadFloorSpaceCatalogs, { floorSpaceId });

        yield put(actions.map.values.global.clearDeletedLights());
        yield put(actions.map.values.global.clearDeletedMarkers());
        yield put(actions.map.values.global.setInitHistory());

        yield put(
            actions.map.general.setSyncingStatus({
                name: "success",
                message: "All changes have been synced to the cloud.",
                lastSavedIndex: 1,
            }),
        );
    } catch {
        const defaultMessage =
            "We couldn't connect to save changes. Check your internet connection.";
        const errorMessage = yield select(
            ({ map }) => map.general.syncingErrorMessage,
        );

        yield put(
            actions.map.general.setSyncingStatus({
                name: "error",
                message: errorMessage ?? defaultMessage,
            }),
        );
    } finally {
        yield delay(7000);
        yield put(actions.map.general.setSyncingStatus({ message: "" }));
        yield put(actions.map.general.setSyncingErrorMessage(null));
    }
}

export default saveChanges;
